import React, { FC, useState, useEffect, useContext } from 'react';

import Button from '@mui/material/Button';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';

import ErrorMessage from '../layout/ErrorMessage';
import SelectInput from '../form/components/SelectInput';
import TextInput from '../form/components/TextInput';
import LabeledToggleButton from '../form/components/LabeledToggleButton';
import LoadingWrapper from '../layout/LoadingWrapper';

import { SnackBarContext } from '../form/contexts/SnackBarContext';

import { titleize } from '../../utils/titleize';

import axios from '../utils/axios.utils';

const inputPropsWithLimit = (field: any, columnLimits: Record<string, number>) => {
  const limit = columnLimits[field.name];
  return limit ? { ...(field.inputProps ?? {}), maxLength: limit } : field.inputProps;
};

interface CreateFormProps {
  fields: any[];
  controller: string;
  modelName: string;
  initialValues?: Record<any, any>;
  onSuccess?: (createdRecord: Record<any, any>) => void;
}

const CreateForm: FC<CreateFormProps> = (props) => {
  const { controller, modelName, fields, initialValues = {}, onSuccess } = props;

  const { setSnackBarOpen, setSnackBarMessage } = useContext(SnackBarContext);

  const [formValues, setFormValues] = useState<Record<any, any>>(initialValues);
  const [columnLimits, setColumnLimits] = useState<Record<string, number>>({});
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [loading, setLoading] = useState<boolean>(false);

  useEffect(() => {
    axios
      .get<string, any>(`/${controller}/new`)
      .then((response) => setColumnLimits(response.data.servman_column_limits || {}))
      .catch(() => {});
  }, [controller]);

  const handleCreate = async () => {
    setLoading(true);
    try {
      const response: any = await axios.post(`/${controller}`, {
        [modelName]: formValues,
      });
      setSnackBarOpen(true);
      setSnackBarMessage('Record Created');
      setErrorMessage('');
      if (onSuccess) {
        onSuccess(response.data.result);
      }
    } catch (error) {
      setErrorMessage('Something went wrong creating the Record');
    } finally {
      setLoading(false);
    }
  };

  return (
    <LoadingWrapper isLoading={loading}>
      <Grid container>
        <Grid item xs={12}>
          <Grid container spacing={2}>
            {fields.map((field) => (
              <Grid key={String(field.name)} item xs={field.xs ?? 12} {...field.gridProps}>
                {field.kind === 'custom' ? (
                  field.render({ formValues, setFormValues })
                ) : field.kind === 'text' ? (
                  <TextInput
                    label={titleize(field.name)}
                    value={formValues?.[field.name] ?? ''}
                    valueChanged={(value: string) =>
                      setFormValues((prev) => ({
                        ...prev,
                        [field.name]: value,
                      }))
                    }
                    shrinkLabel
                    inputProps={inputPropsWithLimit(field, columnLimits)}
                  />
                ) : field.kind === 'toggle' ? (
                  <>
                    <Typography variant="body1">{titleize(field.name)}</Typography>
                    <LabeledToggleButton
                      onLabel={field.onLabel ?? 'On'}
                      offLabel={field.offLabel ?? 'Off'}
                      param={field.name}
                      formValues={formValues}
                      setFormValues={setFormValues}
                    />
                  </>
                ) : (
                  <SelectInput
                    label={titleize(field.name)}
                    choices={field.choices}
                    value={formValues?.[field.name]}
                    valueChanged={(val: string) =>
                      setFormValues((prev) => ({
                        ...prev,
                        [field.name]: val,
                      }))
                    }
                  />
                )}
              </Grid>
            ))}
          </Grid>
        </Grid>
        <Grid item xs={12}>
          <ErrorMessage message={errorMessage} />
        </Grid>
        <Grid item xs={12}>
          <Button variant="contained" style={{ marginTop: 15 }} onClick={handleCreate}>
            Create
          </Button>
        </Grid>
      </Grid>
    </LoadingWrapper>
  );
};

export default CreateForm;
